import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CopyLinkButton } from '../components/CopyLinkButton';
import { api, type CompPublicRulesResponse } from '../lib/api';
import './CompHome.css';

// ─────────────────────────────────────────────────────────────────────────────
// CompRules — public, shareable rules / Code-of-Conduct page.
//
// DATA-DRIVEN (was a hardcoded EGL manual). Fetches the comp's OWN rules from
// GET /public/comp/:id/rules — a truly-public, no-auth endpoint that returns a
// strict allowlist { compName, rulesText } and 404s (opt-in + fail-closed) for
// any comp that hasn't published rules. So this page shows each competition's
// own terms, and un-authored comps get a neutral empty state — never EGL.
//
// rulesText is admin-authored free text, rendered as plain text (pre-wrap
// preserves the author's line breaks). NEVER dangerouslySetInnerHTML — this is
// a public surface; keeping it text-only sidesteps stored-XSS entirely.
//
// Reuses the .comp-home scoped styling so it matches the competition/org pages.
// The "Copy link to share" button is leak-safe: the link it copies resolves to
// this same opt-in, allowlisted, no-PII endpoint for whoever opens it.
// ─────────────────────────────────────────────────────────────────────────────

export function CompRules() {
  const { compId } = useParams<{ compId: string }>();
  const back = compId ? `/comp/${encodeURIComponent(compId)}` : '/';

  const [data, setData] = useState<CompPublicRulesResponse | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!compId) {
      setLoaded(true);
      return;
    }
    api
      .publicCompRules(compId)
      .then(res => {
        if (alive) {
          setData(res);
          setLoaded(true);
        }
      })
      .catch(() => {
        // not_found (disabled / no rules / missing) or any fetch error → neutral
        // empty state. Never fall back to another comp's content.
        if (alive) {
          setData(null);
          setLoaded(true);
        }
      });
    return () => {
      alive = false;
    };
  }, [compId]);

  // While the rules probe is in flight, render the shell only.
  if (!loaded) {
    return <div className="comp-home" />;
  }

  // Neutral empty state — rules not published for this competition.
  if (!data) {
    return (
      <div className="comp-home">
        <header className="topbar">
          <div className="wrap">
            <div className="brand">
              <div className="name">Rules<small>Code of Conduct</small></div>
            </div>
            <Link to={back} className="backlink">← Competition page</Link>
          </div>
        </header>
        <section className="rules-head">
          <div className="wrap">
            <h1>No published rules yet</h1>
            <p className="lead" style={{ marginTop: 10 }}>
              This competition hasn&apos;t published a rules &amp; Code-of-Conduct page. Check back
              soon, or head to the competition page for details.
            </p>
            <div className="cta-row" style={{ marginTop: 18 }}>
              <Link className="btn btn-primary" to={back}>← Competition page</Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Split the author's text into paragraphs on blank lines; pre-wrap preserves
  // single line breaks within each paragraph. Faithful to what they typed.
  const paragraphs = data.rulesText.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);

  return (
    <div className="comp-home">
      {/* Top bar — comp name, no EGL crest */}
      <header className="topbar">
        <div className="wrap">
          <div className="brand">
            <div className="name">{data.compName}<small>Rules &amp; Code of Conduct</small></div>
          </div>
          <Link to={back} className="backlink">← Competition page</Link>
        </div>
      </header>

      {/* Title */}
      <section className="rules-head">
        <div className="wrap">
          <div className="season-tag">Competition rules · Code of Conduct</div>
          <h1>Rules &amp; Code of Conduct</h1>
          <p className="lead" style={{ marginTop: 10 }}>
            By joining {data.compName}, all players and teams agree to the following terms. Share
            this link freely — it opens for anyone.
          </p>
          <div className="cta-row" style={{ marginTop: 18 }}>
            <CopyLinkButton label="Copy link to share" className="btn btn-primary" />
            <Link className="btn btn-ghost" to={back}>← Competition page</Link>
          </div>
        </div>
      </section>

      <div className="wrap">
        <section className="rules-sec">
          {paragraphs.map((p, i) => (
            <p key={i} className="rules-p" style={{ whiteSpace: 'pre-wrap' }}>{p}</p>
          ))}
        </section>

        <div className="cta-row" style={{ marginTop: 8 }}>
          <Link className="btn btn-primary" to={back}>← Back to the competition page</Link>
        </div>
      </div>
    </div>
  );
}
